import { useCallback, useEffect } from 'react';
import { WidgetLayout } from '@/state/clientStorage/clientStorageTypes';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { selectClientStorage } from '@/state/clientStorage/clientStorageSelectors';
import { useGetInitialData } from '@/hooks/initialHooks';
import { setClientStorageState, setIsLoading } from '@/state/clientStorage/clientStorageSlice';
import useSetFilters from '@/hooks/useSetFilters';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';

const useWidgetsData = () => {
    const clientStorage = useSelector((state: RootState) => selectClientStorage(state));
    const { getAllFilters } = useGetInitialData(false);
    const dispatch = useDispatch();
    const { uploadFilters } = useSetFilters();
    const isMemberAuthorized = useIsMemberAuthorized();

    const fetchClientStorage = useCallback(async () => {
        try {
            if (isMemberAuthorized) {
                const filters = await getAllFilters();
                if (filters) {
                    filters.widgetsLayout = filters.widgetsLayout?.map((widget) =>
                        widget.id === 'trending_wallets'
                            ? {
                                  ...widget,
                                  id: 'trending_wallets_subtitle',
                                  label: 'trending_wallets_subtitle',
                              }
                            : widget,
                    );
                    dispatch(setClientStorageState(filters));
                }
            }
        } catch (error) {
            console.error(error);
        }
    }, [isMemberAuthorized, dispatch]);

    const setNewWidgetLayout = async (widgetLayout: WidgetLayout[]) => {
        try {
            if (!isMemberAuthorized) return;
            dispatch(setIsLoading(true));
            await uploadFilters({ widgetsLayout: widgetLayout || [] });
        } catch (error) {
            console.error(error);
        } finally {
            dispatch(setIsLoading(false));
        }
    };

    const setIsLiquidationSoundMuted = async (isMuted: boolean) => {
        try {
            if (!isMemberAuthorized) return;
            await uploadFilters({ isLiquidationSoundMuted: isMuted });
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (!clientStorage) {
            fetchClientStorage();
        }
    }, []);

    return {
        setNewWidgetLayout,
        fetchClientStorage,
        setIsLiquidationSoundMuted,
    };
};

export default useWidgetsData;
